import { useState, useEffect } from "react";
import { useFirebase } from "@/contexts/FirebaseContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { DollarSign, Clock, Users } from "lucide-react";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { toast } from "sonner";

interface Enrollment {
  id: string;
  coursePrice: number;
  hoursCompleted: number;
  createdAt: string;
  studentId: string;
  courseId: string;
  instructorId: string;
}

interface Payment {
  id: string;
  amount: number;
  courseId: string;
  courseName: string;
  createdAt: string;
  currency: string;
  description: string;
  enrollmentId: string;
  status: string;
  userEmail: string;
  userId: string;
  userName: string;
}

interface InstructorPayment {
  id: string;
  instructorId: string;
  paymentId: string;
  enrollmentId: string;
  amount: number;
  amountPaid: number;
  amountRemaining: number;
  hoursTaught: number;
  status: 'paid' | 'pending';
  createdAt: string;
  studentName: string;
  courseName: string;
  installmentNumber: number;
  totalAmount: number;
  paymentStatus: 'paid' | 'pending';
  paymentToInstructor: 'paid' | 'unpaid';
  instructorPaymentRate?: number;
}

const InstructorPayments = () => {
  const { currentUser, getFirestore } = useFirebase();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [totals, setTotals] = useState({
    earned: 0,
    pending: 0,
    hours: 0
  });
  const [payments, setPayments] = useState<InstructorPayment[]>([]);
  
  useEffect(() => {
    const fetchPayments = async () => {
      if (!currentUser?.uid) return;
      
      setLoading(true);
      try {
        const db = getFirestore();
        if (!db) throw new Error("Firestore is not initialized");

        // Fetch instructor payments
        const paymentsRef = collection(db, "instructor_payments");
        const q = query(
          paymentsRef,
          where("instructorId", "==", currentUser.uid),
          orderBy("createdAt", "desc")
        );

        const querySnapshot = await getDocs(q);
        const paymentsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as InstructorPayment[];

        console.log("Fetched instructor payments:", paymentsData);
        setPayments(paymentsData);

        // Calculate totals
        const earned = paymentsData.reduce((sum, p) => sum + p.amountPaid, 0);
        const pending = paymentsData.reduce((sum, p) => sum + p.amountRemaining, 0);
        const hours = paymentsData.reduce((sum, p) => sum + p.hoursTaught, 0);

        console.log("Payment totals:", {
          earned,
          pending,
          hours,
          paymentCount: paymentsData.length
        });

        setTotals({ earned, pending, hours });
      } catch (error) {
        console.error("Error fetching instructor payments:", error);
        toast.error("Failed to load payment data");
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [currentUser, toast]);
  
  const filteredPayments = activeTab === "all" 
    ? payments 
    : payments.filter(p => p.paymentStatus === activeTab);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  
  const getPaymentStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500">Paid</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500">Pending</Badge>;
      default:
        return <Badge className="bg-gray-500">Unknown</Badge>;
    }
  };
  
  return (
    <DashboardLayout role="instructor">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">My Payments</h1>
          <p className="text-gray-500 mt-2">Track your payment status and earnings</p>
        </div>
        
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="mr-4 bg-green-100 p-3 rounded-full">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Earned</p>
                  {loading ? (
                    <Skeleton className="h-8 w-28" />
                  ) : (
                    <h3 className="text-2xl font-bold text-gray-900">
                      {formatCurrency(totals.earned)}
                    </h3>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="mr-4 bg-blue-100 p-3 rounded-full">
                  <Clock className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Hours Taught</p>
                  {loading ? (
                    <Skeleton className="h-8 w-28" />
                  ) : (
                    <h3 className="text-2xl font-bold text-gray-900">
                      {totals.hours} hrs
                    </h3>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="mr-4 bg-amber-100 p-3 rounded-full">
                  <DollarSign className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Pending Payments</p>
                  {loading ? (
                    <Skeleton className="h-8 w-28" />
                  ) : (
                    <h3 className="text-2xl font-bold text-gray-900">
                      {formatCurrency(totals.pending)}
                    </h3>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="mr-4 bg-purple-100 p-3 rounded-full">
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Assigned Students</p>
                  {loading ? (
                    <Skeleton className="h-8 w-28" />
                  ) : (
                    <h3 className="text-2xl font-bold text-gray-900">
                      {payments.length}
                    </h3>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle>Payment History</CardTitle>
            <CardDescription>
              View all your payments from students
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="paid">Paid</TabsTrigger>
                <TabsTrigger value="pending">Pending</TabsTrigger>
              </TabsList>
              <TabsContent value={activeTab}>
                {loading ? (
                  <div className="space-y-4">
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                  </div>
                ) : filteredPayments.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No payments found</p>
                  </div>
                ) : (
                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Student</TableHead>
                          <TableHead>Course</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredPayments.map((payment) => (
                          <TableRow key={payment.id}>
                            <TableCell>{payment.studentName}</TableCell>
                            <TableCell>{payment.courseName}</TableCell>
                            <TableCell>{formatCurrency(payment.amountPaid)}</TableCell>
                            <TableCell>
                              {getPaymentStatusBadge(payment.status)}
                            </TableCell>
                            <TableCell>
                              {format(new Date(payment.createdAt), "MMM d, yyyy")}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default InstructorPayments;

